// PlanYears management panel on the Account detail.
//
// Shows active PlanYears (current first, then ``effective_start
// desc``), with an archived group collapsed by default. Each row
// links into the PlanYear detail page (FE Task 6) and exposes
// inline Edit / Archive / Restore. The Create modal seeds the
// document folders server-side via POST /plan_years/.

import {
  ArchiveRestore, ArchiveX, Pencil, Plus,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  deriveDefaultEffectiveEnd, validatePlanYearDates,
} from "./planYearForm";
import {
  useArchivePlanYearMutation, useCreatePlanYearMutation,
  useRestorePlanYearMutation, useUpdatePlanYearMutation,
  usePlanYearListQuery,
} from "./planYearHooks";
import type { PlanYearRow } from "./planYearApi";
import { formatPlanYearDate, formatPlanYearRange } from "./dateFormat";


export interface AccountPlanYearsPanelProps {
  accountId: string;
}


export function AccountPlanYearsPanel({
  accountId,
}: AccountPlanYearsPanelProps) {
  const listQuery = usePlanYearListQuery(accountId, "all");
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PlanYearRow | null>(null);
  const [archiveConfirm, setArchiveConfirm] = useState<PlanYearRow | null>(null);

  const createMutation = useCreatePlanYearMutation(accountId);
  const updateMutation = useUpdatePlanYearMutation(accountId);
  const archiveMutation = useArchivePlanYearMutation(accountId);
  const restoreMutation = useRestorePlanYearMutation(accountId);

  const { active, archived } = useMemo(() => {
    const rows = listQuery.data?.rows ?? [];
    const a: PlanYearRow[] = [];
    const ar: PlanYearRow[] = [];
    for (const r of rows) {
      if (r.is_archived) ar.push(r);
      else a.push(r);
    }
    return { active: a, archived: ar };
  }, [listQuery.data]);

  return (
    <section
      data-testid="account-plan-years-panel"
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-canvas/40 p-4"
    >
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-text-primary">
          Plan years
        </h2>
        <button
          type="button"
          data-testid="plan-years-new"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1 rounded-md bg-teal px-2 py-1 text-xs font-medium text-text-inverse hover:opacity-90"
        >
          <Plus size={12} aria-hidden="true" />
          New plan year
        </button>
      </header>

      {listQuery.isLoading ? (
        <p className="text-xs italic text-text-tertiary">Loading…</p>
      ) : listQuery.isError ? (
        <p
          data-testid="plan-years-error"
          role="alert"
          className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember-ink"
        >
          Couldn't load plan years.
        </p>
      ) : (
        <>
          {active.length === 0 ? (
            <p
              data-testid="plan-years-empty"
              className="text-xs italic text-text-tertiary"
            >
              No plan years on file yet.
            </p>
          ) : (
            <ul role="list" data-testid="plan-years-active-list" className="flex flex-col divide-y divide-border-subtle">
              {active.map((row) => (
                <PlanYearRowItem
                  key={row.plan_year_id}
                  accountId={accountId}
                  row={row}
                  onEdit={() => setEditing(row)}
                  onArchive={() => setArchiveConfirm(row)}
                />
              ))}
            </ul>
          )}

          {archived.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                data-testid="plan-years-toggle-archived"
                aria-expanded={showArchived}
                onClick={() => setShowArchived((v) => !v)}
                className="text-xs font-medium text-text-secondary hover:text-text-primary"
              >
                {showArchived ? "Hide" : "Show"} archived (
                {archived.length})
              </button>
              {showArchived && (
                <ul
                  role="list"
                  data-testid="plan-years-archived-list"
                  className="mt-2 flex flex-col divide-y divide-border-subtle opacity-80"
                >
                  {archived.map((row) => (
                    <PlanYearRowItem
                      key={row.plan_year_id}
                      accountId={accountId}
                      row={row}
                      onEdit={() => setEditing(row)}
                      onRestore={() =>
                        restoreMutation.mutate(row.plan_year_id)
                      }
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {creating && (
        <PlanYearFormModal
          mode="create"
          isPending={createMutation.isPending}
          onClose={() => setCreating(false)}
          onSubmit={(values) => {
            createMutation.mutate(values, {
              onSuccess: () => setCreating(false),
            });
          }}
        />
      )}

      {editing && (
        <PlanYearFormModal
          mode="edit"
          initial={editing}
          isPending={updateMutation.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(values) => {
            updateMutation.mutate(
              { planYearId: editing.plan_year_id, values },
              { onSuccess: () => setEditing(null) },
            );
          }}
        />
      )}

      {archiveConfirm && (
        <ConfirmModal
          title="Archive plan year?"
          body={
            <p className="text-sm text-text-secondary">
              {archiveConfirm.is_current
                ? `Archiving ${formatRange(archiveConfirm)} clears the current flag. The plan year stays visible under "Show archived".`
                : `${formatRange(archiveConfirm)} will move to the archived group.`}
            </p>
          }
          confirmLabel="Archive"
          isPending={archiveMutation.isPending}
          onConfirm={() => {
            archiveMutation.mutate(archiveConfirm.plan_year_id, {
              onSuccess: () => setArchiveConfirm(null),
            });
          }}
          onCancel={() => setArchiveConfirm(null)}
        />
      )}
    </section>
  );
}


function PlanYearRowItem({
  accountId, row, onEdit, onArchive, onRestore,
}: {
  accountId: string;
  row: PlanYearRow;
  onEdit: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
}) {
  return (
    <li
      data-testid={`plan-year-row-${row.plan_year_id}`}
      data-current={row.is_current ? "true" : "false"}
      data-archived={row.is_archived ? "true" : "false"}
      className="flex items-center gap-3 px-1 py-2"
    >
      <Link
        to={`/dashboard/accounts/${accountId}/plan_years/${row.plan_year_id}`}
        data-testid={`plan-year-row-link-${row.plan_year_id}`}
        className="flex-1 text-sm text-text-primary hover:underline"
      >
        {formatRange(row)}
      </Link>
      <span
        data-testid={`plan-year-row-renewal-${row.plan_year_id}`}
        className="text-[11px] text-text-tertiary"
      >
        Ends {formatDate(row.effective_end)}
      </span>
      {row.is_current && (
        <span className="rounded-full bg-sage-tint px-2 py-0.5 text-[11px] font-medium text-teal">
          Current
        </span>
      )}
      {row.is_archived && (
        <span className="rounded-full bg-recessed px-2 py-0.5 text-[11px] text-text-secondary">
          Archived
        </span>
      )}
      <span className="text-[11px] text-text-tertiary">
        {row.open_commitments} open · {row.done_commitments} done
        {row.archived_commitments > 0 ? ` · ${row.archived_commitments} archived` : ""}
      </span>
      <button
        type="button"
        data-testid={`plan-year-row-edit-${row.plan_year_id}`}
        aria-label="Edit plan year"
        onClick={onEdit}
        className="text-text-tertiary hover:text-text-primary"
      >
        <Pencil size={12} aria-hidden="true" />
      </button>
      {onArchive && (
        <button
          type="button"
          data-testid={`plan-year-row-archive-${row.plan_year_id}`}
          aria-label="Archive plan year"
          onClick={onArchive}
          className="group text-text-tertiary"
        >
          <ArchiveX
            size={12}
            aria-hidden="true"
            className="group-hover:stroke-ember"
          />
        </button>
      )}
      {onRestore && (
        <button
          type="button"
          data-testid={`plan-year-row-restore-${row.plan_year_id}`}
          aria-label="Restore plan year"
          onClick={onRestore}
          className="text-text-tertiary hover:text-text-primary"
        >
          <ArchiveRestore size={12} aria-hidden="true" />
        </button>
      )}
    </li>
  );
}


export interface PlanYearFormValues {
  effective_start: string;
  effective_end: string;
  open_enrollment_start: string;
  open_enrollment_end: string;
  is_current: boolean;
  notes: string;
}


function PlanYearFormModal({
  mode, initial, isPending, onClose, onSubmit,
}: {
  mode: "create" | "edit";
  initial?: PlanYearRow;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (input: import("./planYearApi").PlanYearCreateInput) => void;
}) {
  const [values, setValues] = useState<PlanYearFormValues>(() => ({
    effective_start: initial?.effective_start ?? "",
    effective_end: initial?.effective_end ?? "",
    open_enrollment_start: initial?.open_enrollment_start ?? "",
    open_enrollment_end: initial?.open_enrollment_end ?? "",
    is_current: initial?.is_current ?? false,
    notes: initial?.notes ?? "",
  }));
  // Track whether the user has manually touched ``effective_end``.
  // While this stays false, typing into ``effective_start`` autofills
  // the end via ``deriveDefaultEffectiveEnd``. Initialise to ``true``
  // when editing an existing row so we never clobber a persisted end.
  const [endTouched, setEndTouched] = useState<boolean>(
    Boolean(initial?.effective_end),
  );
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof PlanYearFormValues>(
    k: K, v: PlanYearFormValues[K],
  ) {
    setValues((p) => ({ ...p, [k]: v }));
  }

  function setEffectiveStart(next: string) {
    setValues((prev) => {
      const updated = { ...prev, effective_start: next };
      if (!endTouched) {
        const autofill = deriveDefaultEffectiveEnd(next);
        if (autofill) updated.effective_end = autofill;
      }
      return updated;
    });
  }

  function setEffectiveEnd(next: string) {
    setEndTouched(true);
    set("effective_end", next);
  }

  function handleSubmit() {
    if (!values.effective_start || !values.effective_end) {
      setError("Effective start and effective end are required.");
      return;
    }
    const dateCheck = validatePlanYearDates(values);
    if (!dateCheck.ok && dateCheck.error) {
      setError(dateCheck.error);
      return;
    }
    setError(null);
    onSubmit({
      effective_start: values.effective_start,
      effective_end: values.effective_end,
      open_enrollment_start: values.open_enrollment_start || null,
      open_enrollment_end: values.open_enrollment_end || null,
      // ``is_current`` is derived from today's date on the BE — the
      // form intentionally does not send it.
      notes: values.notes || null,
    });
  }

  return (
    <div
      data-testid="plan-year-form-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-canvas/80"
    >
      <div className="flex w-[420px] flex-col gap-3 rounded-md border border-border-subtle bg-raised p-4 shadow-lg">
        <h3 className="text-sm font-semibold text-text-primary">
          {mode === "create" ? "New plan year" : "Edit plan year"}
        </h3>
        {error && (
          <p
            role="alert"
            data-testid="plan-year-form-error"
            className="rounded-md bg-ember-tint px-3 py-1.5 text-xs text-ember-ink"
          >
            {error}
          </p>
        )}
        <DateField
          name="effective_start" label="Effective start"
          value={values.effective_start}
          onChange={setEffectiveStart}
        />
        <DateField
          name="effective_end" label="Effective end"
          value={values.effective_end}
          onChange={setEffectiveEnd}
        />
        <DateField
          name="open_enrollment_start"
          label="Open enrollment start (optional)"
          value={values.open_enrollment_start}
          onChange={(v) => set("open_enrollment_start", v)}
        />
        <DateField
          name="open_enrollment_end"
          label="Open enrollment end (optional)"
          value={values.open_enrollment_end}
          onChange={(v) => set("open_enrollment_end", v)}
        />
        {/* "Current" is derived from today's date — see
            ``accounts.is_current`` on the BE. The form no longer
            offers a manual flag; the helper line spells out the
            new contract so authors know what to expect. */}
        <p className="text-xs text-text-tertiary">
          The plan year becomes "current" automatically when today
          falls within its coverage period.
        </p>
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            data-testid="plan-year-form-cancel"
            onClick={onClose}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="plan-year-form-submit"
            disabled={isPending}
            onClick={handleSubmit}
            className="rounded-md bg-teal px-3 py-1.5 text-xs font-medium text-text-inverse hover:opacity-90 disabled:opacity-50"
          >
            {isPending
              ? "Saving…"
              : mode === "create" ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


function DateField({
  name, label, value, onChange,
}: {
  name: string; label: string; value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-text-secondary">
      {label}
      <input
        type="date"
        data-testid={`plan-year-form-${name}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-sm text-text-primary"
      />
    </label>
  );
}


function ConfirmModal({
  title, body, confirmLabel, isPending, onConfirm, onCancel,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      data-testid="plan-year-confirm-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-canvas/80"
    >
      <div className="flex w-[360px] flex-col gap-3 rounded-md border border-border-subtle bg-raised p-4 shadow-lg">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        {body}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="plan-year-confirm-cancel"
            onClick={onCancel}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="plan-year-confirm-submit"
            disabled={isPending}
            onClick={onConfirm}
            className="rounded-md bg-ember px-3 py-1.5 text-xs font-medium text-text-inverse hover:opacity-90 disabled:opacity-50"
          >
            {isPending ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


// Date helpers live in ``./dateFormat`` so every plan-year surface
// renders YYYY-MM-DD strings TZ-safely (parsing as local-midnight
// instead of UTC, otherwise west-of-UTC users see "Dec 30, 2026"
// for a stored "2026-12-31").
function formatDate(iso: string | null): string {
  return formatPlanYearDate(iso);
}


function formatRange(row: PlanYearRow): string {
  return formatPlanYearRange(row.effective_start, row.effective_end);
}
